import { useState } from "react";
import { Star, Square } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useForceEnd,
  useSetFeatured,
  useStreams,
} from "../../api/hooks.js";
import type { AdminStream } from "../../api/types.js";
import {
  Button,
  CenteredSpinner,
  DriverBadge,
  EmptyState,
  ErrorState,
  PageHeader,
  StatusBadge,
  table,
} from "../../components/ui.js";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs.js";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { toast } from "../../components/toast.js";

function FeaturedToggle({ stream }: { stream: AdminStream }) {
  const setFeatured = useSetFeatured();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setFeatured.mutate(
              { id: stream.id, featured: !stream.featured },
              {
                onSuccess: () =>
                  toast(
                    stream.featured ? "Unfeatured" : "Featured on Home",
                    "success",
                  ),
              },
            );
          }}
          disabled={setFeatured.isPending}
          aria-label={stream.featured ? "Unfeature" : "Feature"}
          className="rounded-lg p-1.5 outline-none transition-colors hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          <Star
            className={cn(
              "h-4 w-4 transition-colors",
              stream.featured
                ? "fill-amber-400 text-amber-400"
                : "text-muted-foreground hover:text-foreground",
            )}
          />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {stream.featured ? "Unfeature" : "Feature on Home"}
      </TooltipContent>
    </Tooltip>
  );
}

function ForceEndButton({ stream }: { stream: AdminStream }) {
  const forceEnd = useForceEnd();
  const [confirm, setConfirm] = useState(false);

  async function run() {
    try {
      await forceEnd.mutateAsync(stream.id);
      toast("Stream ended", "success");
    } finally {
      setConfirm(false);
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        onClick={(e) => {
          e.stopPropagation();
          setConfirm(true);
        }}
      >
        <Square className="h-3.5 w-3.5" /> End
      </Button>
      <ConfirmDialog
        open={confirm}
        title="Force-end stream"
        destructive
        confirmLabel="Force end"
        loading={forceEnd.isPending}
        body={
          <>
            End <span className="font-medium text-foreground">{stream.title}</span>{" "}
            now? The publisher will be disconnected.
          </>
        }
        onConfirm={run}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
}

export function StreamsPage() {
  const [tab, setTab] = useState<"live" | "recent">("live");
  const streams = useStreams(tab);
  const items = streams.data ?? [];

  return (
    <div className="p-6 lg:p-8">
      <PageHeader
        eyebrow="Broadcast"
        title="Streams"
        subtitle="Live and recent streams across all nodes."
        actions={
          <Tabs value={tab} onValueChange={(v) => setTab(v as "live" | "recent")}>
            <TabsList>
              <TabsTrigger value="live">live</TabsTrigger>
              <TabsTrigger value="recent">recent</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />

      {streams.isLoading && <CenteredSpinner label="Loading streams…" />}
      {streams.error && <ErrorState message={streams.error.message} />}

      {streams.data && items.length === 0 && (
        <EmptyState
          title={tab === "live" ? "No live streams" : "No recent streams"}
        />
      )}

      {items.length > 0 && (
        <div className={table.wrap}>
          <div className={table.scroll}>
            <table className={table.table}>
              <thead className={table.head}>
                <tr>
                  <th className={table.th}>Title</th>
                  <th className={table.th}>Owner</th>
                  <th className={table.th}>Driver</th>
                  <th className={table.th}>Status</th>
                  <th className={table.th}>Viewers</th>
                  <th className={table.th}>Started</th>
                  <th className={cn(table.th, "text-center")}>★</th>
                  <th className={table.th} />
                </tr>
              </thead>
              <tbody className={table.body}>
                {items.map((s) => {
                  const ended = s.status === "ended";
                  return (
                    <tr key={s.id} className={table.rowStatic}>
                      <td className={table.td}>
                        <div className="font-medium text-foreground">
                          {s.title}
                        </div>
                        {s.slug && (
                          <div className="font-mono text-xs text-muted-foreground">
                            /{s.slug}
                          </div>
                        )}
                      </td>
                      <td className={table.td}>
                        <span className="text-muted-foreground">
                          {s.ownerDisplayName}
                        </span>
                      </td>
                      <td className={table.td}>
                        <DriverBadge driver={s.driver} />
                      </td>
                      <td className={table.td}>
                        <StatusBadge status={s.status} />
                      </td>
                      <td className={cn(table.td, "tabular-nums text-foreground/80")}>
                        {s.viewerCount}
                      </td>
                      <td className={cn(table.td, "text-xs text-muted-foreground")}>
                        {s.startedAt
                          ? new Date(s.startedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className={cn(table.td, "text-center")}>
                        <FeaturedToggle stream={s} />
                      </td>
                      <td className={cn(table.td, "text-right")}>
                        {!ended && <ForceEndButton stream={s} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
